/**
 * Signing in to the packaged phone app with Zoho or Google (docs/adr/0038-native-sign-in.md).
 *
 * Google refuses OAuth in an embedded WebView, and Zoho's redirect would land in a browser tab, so the
 * provider flow runs in the SYSTEM browser (`@capacitor/browser`: a Chrome Custom Tab on Android, an
 * SFSafariViewController on iOS) and comes back to the app over the custom scheme
 * `peopleorbit://sso?code=<handoff code>&next=<path>`.
 *
 * ANY app on the phone may claim a custom scheme, so the handoff code is PKCE-bound to this app
 * (RFC 7636, S256): the app keeps a random VERIFIER, sends only its SHA-256 CHALLENGE when the sign-in
 * starts, and shows the verifier again only when it redeems the code (SessionHandoff.tsx). A rogue app
 * that captures the code from the scheme cannot redeem it.
 *
 * Never log the verifier or a code, and keep both out of analytics.
 */

import { Browser } from '@capacitor/browser';
import type { SsoProvider, SsoPurpose } from '@hrms/shared';
import { safeNextPath, ssoStartHref } from './ssoSignIn.js';
import { APP_DOMAIN } from './subdomain.js';

/** The custom URL scheme the app claims (Android intent filter, iOS CFBundleURLTypes). */
export const APP_URL_SCHEME = 'peopleorbit';
/** The "host" of the sign-in return URL: `peopleorbit://sso?...`. */
export const APP_SSO_HOST = 'sso';

/**
 * The verifier lives this long. A sign-IN's round trip through the browser is a minute or two, but a
 * sign-UP needs it again much later: the browser leg is short (just the provider's consent screen),
 * then the app itself peeks it a second time at the end of the company-details form, which a real
 * person can easily spend several minutes on (reading terms, typing a company name, waiting for the
 * SMS code). Generous on purpose — once it expires, register()'s own handoff quietly falls back to
 * UNBOUND (the same posture the plain web flow already has, not a weaker one), so a short TTL would
 * not fail loudly; it would just degrade PKCE for no reason anyone watching would notice.
 */
export const APP_VERIFIER_TTL_MS = 30 * 60_000;

const VERIFIER_KEY = 'sso_app_verifier';

/** A handoff code is 43 base64url characters today; the redeem contract allows 16 to 128. */
const HANDOFF_CODE = /^[A-Za-z0-9_-]{16,128}$/;

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** RFC 7636 S256: `base64url(SHA-256(ASCII(verifier)))`. */
export async function challengeForVerifier(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** A fresh verifier (32 random bytes, 43 characters) and its challenge (43 characters). */
export async function makePkcePair(): Promise<{ verifier: string; challenge: string }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const verifier = base64url(bytes);
  return { verifier, challenge: await challengeForVerifier(verifier) };
}

// Storage can throw (private mode, blocked site data); a missing verifier then simply fails the
// sign-in closed (the API refuses a bound code without it), which is the right outcome.
export function saveAppVerifier(verifier: string, now: number = Date.now()): boolean {
  try {
    window.localStorage.setItem(
      VERIFIER_KEY,
      JSON.stringify({ v: verifier, exp: now + APP_VERIFIER_TTL_MS }),
    );
    return true;
  } catch {
    return false;
  }
}

/** The stored verifier if it has not expired, else null. Does not remove it. */
export function peekAppVerifier(now: number = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(VERIFIER_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { v?: unknown; exp?: unknown };
    if (typeof entry.v !== 'string' || typeof entry.exp !== 'number' || entry.exp <= now) {
      clearAppVerifier();
      return null;
    }
    return entry.v;
  } catch {
    return null;
  }
}

export function clearAppVerifier(): void {
  try {
    window.localStorage.removeItem(VERIFIER_KEY);
  } catch {
    // Nothing to clear if storage is blocked.
  }
}

/** Reads and removes: a verifier is good for one redemption. */
export function takeAppVerifier(now: number = Date.now()): string | null {
  const verifier = peekAppVerifier(now);
  clearAppVerifier();
  return verifier;
}

/**
 * Starts a provider sign-in or sign-up in the system browser. Resolves once the browser is opened;
 * the result comes back as `peopleorbit://sso` (nativeLinks.ts). Rejects if the browser could not be
 * opened, after forgetting the verifier it had just stored.
 *
 * The start URL is ABSOLUTE (`https://app.<APP_DOMAIN>/...`): in the app the page origin is the
 * virtual host the bundle is served from, so a relative path would load inside the WebView.
 */
export async function beginNativeSso(
  provider: SsoProvider,
  purpose: SsoPurpose,
  options: { orgSlug?: string | null; next?: string } = {},
): Promise<void> {
  const { verifier, challenge } = await makePkcePair();
  if (!saveAppVerifier(verifier)) {
    throw new Error('Storage is blocked, so this sign-in could not be started.');
  }
  const href = ssoStartHref(provider, purpose, { ...options, appChallenge: challenge });
  const url = href.startsWith('/') ? `https://app.${APP_DOMAIN}${href}` : href;
  try {
    await Browser.open({ url });
  } catch (error) {
    clearAppVerifier();
    throw error;
  }
}

/**
 * The URL the browser tab opens to hand a finished sign-in — or, for a sign-up still missing its
 * company details, a verified identity to continue with — back to the app. `kind` is omitted from
 * the URL for the ordinary 'session' case (every link before this one looked like that; changing
 * none of them keeps old builds, and every existing reader, working unchanged) and is `signup` only
 * for the one other code this app mints: `redeemSsoSignupHandoff`'s (nativeLinks.ts routes on it).
 */
export function appReturnUrl(code: string, next: string, kind: 'session' | 'signup' = 'session'): string {
  const path = safeNextPath(next) ?? '/';
  const suffix = kind === 'signup' ? '&kind=signup' : '';
  return `${APP_URL_SCHEME}://${APP_SSO_HOST}?code=${encodeURIComponent(code)}&next=${encodeURIComponent(path)}${suffix}`;
}

/**
 * The code, landing path and kind in a `peopleorbit://sso?...` URL, or null for anything else:
 * another scheme or host, a missing or malformed code. `next` only ever comes out as a path of this
 * app. `kind` is present only for the 'signup' case — absent (not `'session'`) for the ordinary one,
 * so every caller written before `kind` existed, which destructures `{ code, next }`, still compiles
 * and still matches exactly the links it always has.
 */
export function parseAppReturnUrl(
  url: string,
): { code: string; next: string; kind?: 'signup' } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== `${APP_URL_SCHEME}:`) return null;
  if (parsed.hostname.toLowerCase() !== APP_SSO_HOST) return null;
  const code = parsed.searchParams.get('code');
  if (!code || !HANDOFF_CODE.test(code)) return null;
  const next = safeNextPath(parsed.searchParams.get('next')) ?? '/';
  return parsed.searchParams.get('kind') === 'signup' ? { code, next, kind: 'signup' } : { code, next };
}
